/**
 * Fetches receipt bytes back from Drive so the local copy is complete again.
 *
 * The bookkeeping lives in IndexedDB; Drive is the backup. A snapshot carries
 * no bytes, so a restored company starts with every attachment marked
 * `hasLocalBlob: 0` — the receipts exist only as files in `Kvitton/<år>/`.
 * Leaving it there would make the primary store the incomplete one, which is
 * backwards, so this walks that flag and downloads what is missing.
 *
 * The work list is a query, not a queue: `[companyId+hasLocalBlob]` names
 * exactly the attachments still to fetch, so the pass resumes by itself after
 * a reload, a lost connection or a closed tab, and finishes when the index is
 * empty. Thumbnails are rebuilt locally from the bytes (`thumbnailLink` is
 * never used).
 */
import { getRemoteStore, isRemoteError } from '@/cloud/store';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { Attachment, Uuid } from '@/domain/types';

/** Pause the pass after this many consecutive failures that are not 404s. */
export const MAX_CONSECUTIVE_FAILURES = 3;

export type HydrationPhase = 'idle' | 'running' | 'paused' | 'needs_sign_in' | 'storage_full';

/**
 * A browser that is out of room throws on the write, after the download has
 * already been paid for. Retrying re-downloads the same file to fail the same
 * way, and "try again later" is the wrong thing to tell someone whose disk is
 * full — so this is terminal for the pass, not a pause.
 */
export function isQuotaError(err: unknown): boolean {
  return err instanceof Error && (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED');
}

export interface HydrationState {
  phase: HydrationPhase;
  /** Attachments still without their bytes. */
  pending: number;
  /** Fetched in this pass. */
  fetched: number;
  /** Gone from Drive: the bytes cannot be recovered. */
  missing: number;
}

export interface HydrationDeps {
  db: BookkeepingDB;
  download: (driveFileId: string) => Promise<Blob>;
  /** Injected so this module does not pull the canvas into tests. */
  makeThumbnail: (blob: Blob) => Promise<Blob>;
}

/**
 * The attachments whose bytes are still only in Drive.
 *
 * Read through `[companyId+hasLocalBlob]`, so the scan touches only rows that
 * have no blob by definition — the expensive ones are never loaded. Rows
 * without a `driveFileId` are left out: those bytes never reached Drive, so
 * there is nothing to fetch and they are not work, they are a loss.
 */
export async function pendingAttachments(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Attachment[]> {
  const rows = await db.attachments.where('[companyId+hasLocalBlob]').equals([companyId, 0]).toArray();
  return rows.filter((a) => !!a.driveFileId);
}

export async function pendingAttachmentIds(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Uuid[]> {
  return (await pendingAttachments(companyId, db)).map((a) => a.id);
}

/** Same, across every company in this browser. */
export async function pendingAttachmentCount(db: BookkeepingDB = defaultDb): Promise<number> {
  const companies = await db.companies.toArray();
  let total = 0;
  for (const c of companies) total += (await pendingAttachments(c.id, db)).length;
  return total;
}

export function createReceiptHydration(partial: Partial<HydrationDeps> = {}) {
  const deps: HydrationDeps = {
    db: partial.db ?? defaultDb,
    download: partial.download ?? ((driveFileId) => getRemoteStore().get(driveFileId)),
    makeThumbnail: partial.makeThumbnail ?? (async (blob) => (await import('./image')).makeThumbnail(blob)),
  };
  const listeners = new Set<(s: HydrationState) => void>();
  let state: HydrationState = { phase: 'idle', pending: 0, fetched: 0, missing: 0 };
  let running: Promise<HydrationState> | null = null;

  const emit = (next: Partial<HydrationState>) => {
    state = { ...state, ...next };
    for (const l of listeners) l(state);
  };

  /**
   * Fetch one attachment. A 404 means the file is gone from Drive: clear the
   * id so it is not retried forever and the UI can say the receipt is lost
   * rather than pretending it is on its way.
   */
  async function fetchOne(a: Attachment): Promise<'fetched' | 'missing' | 'failed'> {
    if (!a.driveFileId) return 'missing';
    let blob: Blob;
    try {
      blob = await deps.download(a.driveFileId);
    } catch (err) {
      if (isRemoteError(err) && err.status === 404) {
        await deps.db.attachments.where('id').equals(a.id).modify((row) => void delete row.driveFileId);
        return 'missing';
      }
      throw err;
    }
    let thumbnail = blob;
    try {
      thumbnail = await deps.makeThumbnail(blob);
    } catch {
      // A receipt without a thumbnail still beats a receipt without bytes.
    }
    await deps.db.attachments.update(a.id, { blob, thumbnail, hasLocalBlob: 1, bytes: blob.size });
    return 'fetched';
  }

  /** Walk the pending attachments of one company until the list is empty. */
  async function runFor(companyId: Uuid): Promise<void> {
    let failures = 0;
    for (;;) {
      const waiting = await pendingAttachments(companyId, deps.db);
      emit({ pending: waiting.length });
      const attachment = waiting[0];
      if (!attachment) return;
      let outcome: 'fetched' | 'missing' | 'failed';
      try {
        outcome = await fetchOne(attachment);
      } catch (err) {
        if (isRemoteError(err) && (err.status === 401 || err.status === 403)) {
          emit({ phase: 'needs_sign_in' });
          return;
        }
        if (isQuotaError(err)) {
          // No amount of retrying makes room. Stop the whole pass so the other
          // companies do not each burn three more downloads discovering it.
          emit({ phase: 'storage_full' });
          return;
        }
        outcome = 'failed';
      }
      if (outcome === 'fetched') {
        failures = 0;
        emit({ fetched: state.fetched + 1 });
      } else if (outcome === 'missing') {
        failures = 0;
        emit({ missing: state.missing + 1 });
      } else if (++failures >= MAX_CONSECUTIVE_FAILURES) {
        // Offline, or Drive is unwell. The flag keeps the work; try later.
        emit({ phase: 'paused' });
        return;
      }
    }
  }

  return {
    get state(): HydrationState {
      return state;
    },
    subscribe(listener: (s: HydrationState) => void): () => void {
      listeners.add(listener);
      listener(state);
      return () => void listeners.delete(listener);
    },
    /** Run once over every company; concurrent calls share the same pass. */
    run(): Promise<HydrationState> {
      if (running) return running;
      emit({ phase: 'running', fetched: 0, missing: 0 });
      running = (async () => {
        try {
          for (const company of await deps.db.companies.toArray()) {
            await runFor(company.id);
            if (state.phase !== 'running') break;
          }
          emit({ pending: await pendingAttachmentCount(deps.db) });
          if (state.phase === 'running') emit({ phase: 'idle' });
          return state;
        } finally {
          running = null;
        }
      })();
      return running;
    },
    /** Fetch a single attachment now (the viewer asking for what it needs). */
    async fetch(attachmentId: Uuid): Promise<boolean> {
      const a = await deps.db.attachments.get(attachmentId);
      if (!a || a.hasLocalBlob === 1 || !a.driveFileId) return false;
      try {
        const outcome = await fetchOne(a);
        if (outcome === 'fetched') emit({ fetched: state.fetched + 1, pending: Math.max(0, state.pending - 1) });
        return outcome === 'fetched';
      } catch {
        return false;
      }
    },
  };
}

export type ReceiptHydration = ReturnType<typeof createReceiptHydration>;

export const receiptHydration: ReceiptHydration = createReceiptHydration();
